import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { showNotification } from 'shared/ui/Notification';
import { getShopifyContractId } from 'shared/utils/subscriptionV2Helpers';
import {
  queryKeyForSubscriptionDetailsV2,
  useQueryGetSubscriptionDetailsV2
} from 'state/internal/getSubscriptionDetails';
import { SubscriptionDetailsV2Response } from 'state/internal/getSubscriptionDetails/types';
import { getQueryKeyForGetSubscriptionsV2 } from 'state/internal/getSubscriptions';
import { SubscriptionsResponseV2 } from 'state/internal/getSubscriptions/types';
import { useMutationReactivateSubscriptionV2 } from 'state/internal/reactivateSubscription';
import { ActivateSubscriptionDetailsV2InitialResponse } from 'state/internal/reactivateSubscription/types';
import { useQueryGetToastsData } from 'state/studio/toasts/getToastsData';
import { useStoreValue } from './useStore';
import UpscribeRequestError from './useUpscribeRequest/utils/upscribeRequestError';

type ActivateSubscriptionOptions = {
  subscriptionId?: number;
};

type ActivateSubscriptionCallback = (options?: {
  onSuccess?: (data: ActivateSubscriptionDetailsV2InitialResponse) => void;
  onError?: () => void;
}) => void;

export function useActivateSubscriptionV2({ subscriptionId }: ActivateSubscriptionOptions) {
  const { t } = useTranslation('subscriptionDetails');

  const {
    mutateAsync: activateSubscription,
    isPending,
    isSuccess
  } = useMutationReactivateSubscriptionV2();
  const { data: subscriptionDetails } = useQueryGetSubscriptionDetailsV2(subscriptionId || 0);
  const { payment } = subscriptionDetails || {};
  const store = useStoreValue();
  const queryClient = useQueryClient();

  const content = useQueryGetToastsData().data?.useActivateSubscription;
  const activateSubscriptionCallback: ActivateSubscriptionCallback = useCallback(
    ({ onSuccess, onError } = {}) => {
      if (!subscriptionId) {
        showNotification('mutationError', { title: content?.errorInvalidId });
        return;
      }

      const handleError = (error: UpscribeRequestError) => {
        showNotification('mutationError', { title: error.message });
        if (onError) {
          onError();
        }
      };

      const handleSuccess = (data: ActivateSubscriptionDetailsV2InitialResponse) => {
        if (onSuccess) {
          onSuccess(data);
        }
      };

      if (!payment) {
        handleError(new Error(content?.errorNoPaymentMethod));
        return;
      }

      activateSubscription(
        { subscriptionId },
        {
          onSuccess: (data) => {
            handleSuccess(data);
            showNotification('mutationSuccess', { title: t('v2.reactivate.success') });

            // if Reactivate was successful, update cached data
            // to see a change on the UI without reload

            // update the cached subscription details data
            queryClient.setQueryData(
              queryKeyForSubscriptionDetailsV2(store, subscriptionId),
              (subscription: SubscriptionDetailsV2Response) =>
                subscription && {
                  ...subscription,
                  // ...formatSubscriptionData(response),
                  ...{
                    is_active: true,
                    status: 'ACTIVE'
                  }
                }
            );

            // update the cached subscription list data
            queryClient.setQueryData(
              getQueryKeyForGetSubscriptionsV2(store),
              (subscriptionList: SubscriptionsResponseV2) => {
                // only update the list if it has already been cached
                if (subscriptionList) {
                  const responseId = getShopifyContractId(subscriptionId.toString());
                  const updatedList = subscriptionList.map((subscription) =>
                    getShopifyContractId(subscription.shopify_contract_id) === responseId
                      ? {
                          ...subscription,
                          // ...formatSubscriptionData(response),
                          ...{
                            is_active: true,
                            status: 'ACTIVE'
                          }
                        }
                      : subscription
                  );

                  return updatedList;
                }

                return [];
              }
            );
          }
        }
      );
    },
    [store, activateSubscription, content, payment, subscriptionId, queryClient, t]
  );

  return {
    activateSubscription: activateSubscriptionCallback,
    isPending,
    isSuccess
  };
}
